/**
 * Canvas2D drawing of a constructed armature (docs/SKEL-EDITOR.md §5.1 `draw.ts`, D10, report 01
 * §6.9–6.11).
 *
 * Everything is constructed in SkelForm's y-up world and flipped only here: a `ViewXf` maps world
 * to screen as `(x·scale + tx, −y·scale + ty)`. Per textured bone (report 01 §6.10, the editor's
 * own formula at `renderer.rs:146-148`, identical to `render.py`):
 *   pivot  = R(bone.rot · dir)(pivot_pos ⊙ tex.size) ⊙ bone.scale   dir = −1 when mirrored
 *   centre = bone.pos + pivot,  rotation = bone.rot + pivot_rot · dir,  scale = bone.scale ⊙ pivot_scale
 * The `· dir` on pivot_rot is the SkelForm runtime docs' form (dev-docs engine/draw.md). The editor
 * adds pivot_rot unsigned, which draws a mirrored bone with pivot_rot ≠ 0 rotated by 2·pivot_rot —
 * upside down for our assembled parts (pivot_rot = −θ). Identical for unmirrored bones.
 * The report's shorter R(rot)(pivot_pos ⊙ size ⊙ scale) is equal only for |sx| = |sy|; with the
 * editor form a non-uniform squash (idle/jump torso ScaleY) scales the pivot offset along the same
 * world axes as the upright quad, so a part anchored at its joint stays anchored there.
 * On screen the image is drawn centred with rotation −(rot) — the y flip conjugates rotations.
 * Draw order is a stable sort on (zindex, bone index), lower first. Textures resolve by NAME
 * through the active styles in priority order (default: every style, file order). Tints use a
 * cached offscreen copy of the texture multiplied per pixel (Canvas2D has no per-draw colour
 * matrix, and a 'multiply' composite haloes soft edges — see `tintedSource`), and
 * mesh visuals are drawn as affine-textured triangles whose clip is grown ~0.6 px to hide seams.
 * Every entry point is a no-op for a null context, which is what jsdom returns.
 */
import { constructVerts, isFacingLeft, type WorldBone } from './construct'
import { add, mul2, rot, sub } from './math'
import type { Pose } from './animate'
import type { SkfRoot, SkfTexture, SkfVisuals, SkelCamera, Tint, V2 } from './types'

export type Ctx2D = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D

/** screen = (x·scale + tx, −y·scale + ty); `scale` = screen px per world px. */
export interface ViewXf {
  scale: number
  tx: number
  ty: number
}

export function worldToScreen(view: ViewXf, p: V2): V2 {
  return { x: p.x * view.scale + view.tx, y: -p.y * view.scale + view.ty }
}

export function screenToWorld(view: ViewXf, p: V2): V2 {
  const s = view.scale === 0 ? 1 : view.scale
  return { x: (p.x - view.tx) / s, y: -(p.y - view.ty) / s }
}

/** The view that puts camera point (x, y) at the canvas centre at `zoom` px per world px. */
export function viewFromCamera(cam: SkelCamera, width: number, height: number): ViewXf {
  return { scale: cam.zoom, tx: width / 2 - cam.x * cam.zoom, ty: height / 2 + cam.y * cam.zoom }
}

export interface Bounds {
  minX: number
  minY: number
  maxX: number
  maxY: number
}

/** A view that fits world `bounds` into a width × height canvas with `pad` screen px around it. */
export function fitView(bounds: Bounds, width: number, height: number, pad = 24, maxScale = 8): ViewXf {
  const bw = Math.max(bounds.maxX - bounds.minX, 1e-6)
  const bh = Math.max(bounds.maxY - bounds.minY, 1e-6)
  const scale = Math.min(Math.max((width - 2 * pad) / bw, 1e-4), Math.max((height - 2 * pad) / bh, 1e-4), maxScale)
  const cx = (bounds.minX + bounds.maxX) / 2
  const cy = (bounds.minY + bounds.maxY) / 2
  return { scale, tx: width / 2 - cx * scale, ty: height / 2 + cy * scale }
}

// ------------------------------------------------------------------------------------- quads

/** A textured quad in WORLD space (y-up): centre, rotation (CCW) and the scale applied to (w, h). */
export interface Quad {
  cx: number
  cy: number
  rot: number
  sx: number
  sy: number
}

/** Texture centre offset from the bone origin in world space (report 01 §6.10 step 3, editor form). */
export function pivotOffset(bone: WorldBone, visual: SkfVisuals, texSize: V2): V2 {
  const dir = isFacingLeft(bone.scale) ? -1 : 1
  return mul2(rot(mul2(visual.pivot_pos, texSize), bone.rot * dir), bone.scale)
}

/**
 * Inverse of `pivotOffset`: the `pivot_pos` that puts the texture centre at world point `centre`
 * for this bone (the pivot tool, and "move the joint, keep the art in place").
 */
export function pivotPosForCentre(bone: WorldBone, texSize: V2, centre: V2): V2 {
  const dir = isFacingLeft(bone.scale) ? -1 : 1
  const off = sub(centre, bone.pos)
  const unscaled = { x: bone.scale.x === 0 ? 0 : off.x / bone.scale.x, y: bone.scale.y === 0 ? 0 : off.y / bone.scale.y }
  const local = rot(unscaled, -bone.rot * dir)
  return { x: texSize.x === 0 ? 0 : local.x / texSize.x, y: texSize.y === 0 ? 0 : local.y / texSize.y }
}

export function quadOf(bone: WorldBone, visual: SkfVisuals, texSize: V2): Quad {
  const c = add(bone.pos, pivotOffset(bone, visual, texSize))
  const ps = visual.pivot_scale ?? { x: 1, y: 1 }
  const dir = isFacingLeft(bone.scale) ? -1 : 1
  return { cx: c.x, cy: c.y, rot: bone.rot + (visual.pivot_rot ?? 0) * dir, sx: bone.scale.x * ps.x, sy: bone.scale.y * ps.y }
}

/** World corners of a quad: top-left, top-right, bottom-right, bottom-left of the IMAGE (uv 00,10,11,01). */
export function quadCorners(q: Quad, texSize: V2): V2[] {
  const hw = texSize.x / 2
  const hh = texSize.y / 2
  const c = { x: q.cx, y: q.cy }
  return [
    { x: -hw, y: hh },
    { x: hw, y: hh },
    { x: hw, y: -hh },
    { x: -hw, y: -hh },
  ].map((p) => add(c, rot({ x: p.x * q.sx, y: p.y * q.sy }, q.rot)))
}

/** World → texture pixel (y-down, 0..w × 0..h) through the inverse quad transform. */
export function worldToTexel(q: Quad, texSize: V2, p: V2): V2 {
  const l = rot(sub(p, { x: q.cx, y: q.cy }), -q.rot)
  const lx = q.sx === 0 ? Infinity : l.x / q.sx
  const ly = q.sy === 0 ? Infinity : l.y / q.sy
  return { x: lx + texSize.x / 2, y: texSize.y / 2 - ly }
}

/** World positions of a mesh visual's vertices including the pivot offset, or null for a quad. */
export function meshWorld(root: SkfRoot, world: WorldBone[], boneId: number, texSize: V2): V2[] | null {
  const verts = constructVerts(root, world, boneId)
  const def = root.bones[boneId]
  const visual = def && def.visuals_id >= 0 ? root.visuals[def.visuals_id] : undefined
  const bone = world[boneId]
  if (!verts || !visual || !bone || !visual.indices || visual.indices.length < 3) return null
  const off = pivotOffset(bone, visual, texSize)
  return verts.map((v) => add(v, off))
}

// ------------------------------------------------------------------------------- order + textures

export interface ResolvedTexture {
  styleId: number
  tex: SkfTexture
}

/** What `drawStylesOf` reads: a project view, or a story bundle's puppet view. */
export interface WornStylesSource {
  active_styles?: readonly number[] | null
  root?: { styles?: readonly unknown[] | null } | null
}

/**
 * The outfits (style ids, priority order) a view is DRAWN with — the one rule shared by the editor
 * canvas, the outfit dialog's previews, project thumbnails and the story player (docs/SKEL-EDITOR.md
 * §9.4): the view's `active_styles`, keeping only ids of styles that exist (first occurrence wins).
 * A missing or empty list — or one naming no existing style — is `null`, which `resolveTexture`
 * reads as every style in file order. A view is never drawn with no outfit at all.
 */
export function drawStylesOf(view: WornStylesSource | null | undefined): number[] | null {
  const list = view?.active_styles
  if (!list || !list.length) return null
  const n = view?.root?.styles?.length
  const out: number[] = []
  for (const i of list) {
    if (!Number.isInteger(i) || i < 0 || (n !== undefined && i >= n) || out.includes(i)) continue
    out.push(i)
  }
  return out.length ? out : null
}

/** First texture named `name` in the active styles (priority order; default all, file order). */
export function resolveTexture(root: SkfRoot, name: string, activeStyles?: readonly number[] | null): ResolvedTexture | null {
  if (!name) return null
  const ids = activeStyles ?? root.styles.map((_, i) => i)
  for (const sid of ids) {
    const style = root.styles[sid]
    if (!style) continue
    const tex = style.textures.find((t) => t.name === name)
    if (tex) return { styleId: sid, tex }
  }
  return null
}

function hiddenFlags(root: SkfRoot, pose: Pose, world?: WorldBone[] | null): boolean[] {
  if (world) return world.map((w) => w.hidden)
  const out: boolean[] = []
  root.bones.forEach((b, i) => {
    const p = b.parent_id
    out.push((pose.bones[i]?.hidden ?? false) || (p >= 0 && p < i && out[p] === true))
  })
  return out
}

/**
 * Drawable bone indices, back to front: stable by (zindex, index); hidden bones (propagated) and
 * untextured bones are skipped. Pass `world` to reuse its propagated hidden flags.
 */
export function drawOrder(root: SkfRoot, pose: Pose, world?: WorldBone[] | null): number[] {
  const hidden = hiddenFlags(root, pose, world)
  const items: { id: number; z: number }[] = []
  root.bones.forEach((b, i) => {
    if (b.visuals_id < 0 || hidden[i]) return
    const pv = pose.visuals[b.visuals_id]
    const tex = pv?.tex ?? root.visuals[b.visuals_id]?.tex ?? ''
    if (!tex) return
    items.push({ id: i, z: pv?.zindex ?? root.visuals[b.visuals_id]?.zindex ?? 0 })
  })
  items.sort((a, b) => a.z - b.z || a.id - b.id)
  return items.map((e) => e.id)
}

/** World-space bounds of every drawn texture (quads and meshes); null when nothing is drawn. */
export function armatureBounds(root: SkfRoot, pose: Pose, world: WorldBone[], activeStyles?: readonly number[] | null): Bounds | null {
  let b: Bounds | null = null
  const addPt = (p: V2): void => {
    if (!Number.isFinite(p.x) || !Number.isFinite(p.y)) return
    if (!b) b = { minX: p.x, minY: p.y, maxX: p.x, maxY: p.y }
    else {
      b.minX = Math.min(b.minX, p.x)
      b.minY = Math.min(b.minY, p.y)
      b.maxX = Math.max(b.maxX, p.x)
      b.maxY = Math.max(b.maxY, p.y)
    }
  }
  for (const id of drawOrder(root, pose, world)) {
    const def = root.bones[id]!
    const pv = pose.visuals[def.visuals_id]
    const visual = root.visuals[def.visuals_id]
    const res = resolveTexture(root, pv?.tex ?? visual?.tex ?? '', activeStyles)
    if (!res || !visual || !(res.tex.size.x > 0 && res.tex.size.y > 0)) continue
    const mesh = meshWorld(root, world, id, res.tex.size)
    if (mesh) mesh.forEach(addPt)
    else quadCorners(quadOf(world[id]!, visual, res.tex.size), res.tex.size).forEach(addPt)
  }
  return b
}

// ------------------------------------------------------------------------------------ drawing

export type ImageLookup = (styleId: number, name: string) => CanvasImageSource | null | undefined

export interface DrawOptions {
  view: ViewXf
  activeStyles?: readonly number[] | null
  /** Global opacity multiplier (onion skins use 0.4). */
  alpha?: number
  /** When set, every other bone is drawn at `dimAlpha` (isolate mode). */
  dimExcept?: number | readonly number[] | null
  dimAlpha?: number
  /** Replaces every part's tint (onion skins: red/blue). */
  tintOverride?: Tint | null
}

interface Rect {
  x: number
  y: number
  w: number
  h: number
}

function imageSize(img: CanvasImageSource): V2 | null {
  const o = img as unknown as Record<string, unknown>
  const w = (typeof o.naturalWidth === 'number' && o.naturalWidth) || (typeof o.width === 'number' ? o.width : 0)
  const h = (typeof o.naturalHeight === 'number' && o.naturalHeight) || (typeof o.height === 'number' ? o.height : 0)
  return w > 0 && h > 0 ? { x: w, y: h } : null
}

/**
 * The source rect of a texture inside its image: the atlas rect when the image is an atlas that
 * contains it, else the whole image (a per-texture PNG inside a project has offset (0,0)).
 */
export function sourceRect(img: CanvasImageSource, tex: SkfTexture): Rect {
  const size = imageSize(img)
  const off = tex.offset
  if (!size) return { x: off.x, y: off.y, w: tex.size.x, h: tex.size.y }
  const fits = size.x >= off.x + tex.size.x && size.y >= off.y + tex.size.y
  const isAtlas = off.x > 0 || off.y > 0 || size.x > tex.size.x || size.y > tex.size.y
  if (fits && isAtlas) return { x: off.x, y: off.y, w: tex.size.x, h: tex.size.y }
  return { x: 0, y: 0, w: size.x, h: size.y }
}

type Scratch = HTMLCanvasElement | OffscreenCanvas

function makeCanvas(w: number, h: number): Scratch | null {
  try {
    if (typeof OffscreenCanvas !== 'undefined') return new OffscreenCanvas(w, h)
    if (typeof document !== 'undefined') {
      const c = document.createElement('canvas')
      c.width = w
      c.height = h
      return c
    }
  } catch {
    return null
  }
  return null
}

function contextOf(c: Scratch): Ctx2D | null {
  try {
    if (typeof OffscreenCanvas !== 'undefined' && c instanceof OffscreenCanvas) return c.getContext('2d')
    return (c as HTMLCanvasElement).getContext('2d')
  } catch {
    return null
  }
}

const TINT_CACHE = new WeakMap<object, Map<string, Scratch>>()
const TINT_CACHE_PER_IMAGE = 24

function isWhite(t: Tint): boolean {
  return t.r >= 0.999 && t.g >= 0.999 && t.b >= 0.999
}

function to255(v: number): number {
  return Math.round(Math.min(Math.max(v, 0), 1) * 255)
}

/**
 * Multiply every pixel's colour by `tint` in place (alpha untouched) — `rgb · tint` on
 * straight-alpha data, exactly what `render.py`'s `_apply_tint` and SkelForm's shader do. False
 * when the pixels cannot be read (a tainted canvas, or no ImageData support).
 */
function multiplyPixels(g: Ctx2D, w: number, h: number, tint: Tint): boolean {
  try {
    const data = g.getImageData(0, 0, w, h) as ImageData | undefined
    const px = data?.data
    if (!data || !px || px.length < w * h * 4) return false
    const f = [tint.r, tint.g, tint.b].map((v) => Math.min(Math.max(v, 0), 1)) as [number, number, number]
    for (let i = 0; i < px.length; i += 4) {
      px[i] = Math.round(px[i]! * f[0])
      px[i + 1] = Math.round(px[i + 1]! * f[1])
      px[i + 2] = Math.round(px[i + 2]! * f[2])
    }
    g.putImageData(data, 0, 0)
    return true
  } catch {
    return false
  }
}

/**
 * A copy of `rect` of `img` with its colour multiplied by `tint` (alpha preserved), cached per
 * image + rect + 8-bit tint. Null when no scratch canvas can be made (the caller draws untinted).
 *
 * The multiply is done on the pixels. A `'multiply'` fill composited over the image is NOT
 * equivalent: blend modes mix the fill colour into partly transparent pixels
 * (`(1 − α)·T + α·C·T`), so the anti-aliased dark outline of a tinted part (the side view's far
 * leg, onion skins) turned into a light halo — measured in Chromium, a black α 128 texel came out
 * (102, 102, 108) instead of (0, 0, 0). The composite path remains only as the fallback for an
 * unreadable (tainted) canvas.
 */
export function tintedSource(img: CanvasImageSource, rect: Rect, tint: Tint): Scratch | null {
  const key = `${rect.x},${rect.y},${rect.w},${rect.h}|${to255(tint.r)},${to255(tint.g)},${to255(tint.b)}`
  const per = TINT_CACHE.get(img as object)
  const hit = per?.get(key)
  if (hit) return hit
  const w = Math.max(1, Math.round(rect.w))
  const h = Math.max(1, Math.round(rect.h))
  const canvas = makeCanvas(w, h)
  const g = canvas ? contextOf(canvas) : null
  if (!canvas || !g) return null
  g.drawImage(img, rect.x, rect.y, rect.w, rect.h, 0, 0, w, h)
  if (!multiplyPixels(g, w, h, tint)) {
    g.globalCompositeOperation = 'multiply'
    g.fillStyle = `rgb(${to255(tint.r)},${to255(tint.g)},${to255(tint.b)})`
    g.fillRect(0, 0, w, h)
    g.globalCompositeOperation = 'destination-in'
    g.drawImage(img, rect.x, rect.y, rect.w, rect.h, 0, 0, w, h)
    g.globalCompositeOperation = 'source-over'
  }
  const map = per ?? new Map<string, Scratch>()
  if (map.size >= TINT_CACHE_PER_IMAGE) {
    const first = map.keys().next().value
    if (first !== undefined) map.delete(first)
  }
  map.set(key, canvas)
  if (!per) TINT_CACHE.set(img as object, map)
  return canvas
}

function dimmed(id: number, dimExcept: DrawOptions['dimExcept']): boolean {
  if (dimExcept === null || dimExcept === undefined) return false
  if (typeof dimExcept === 'number') return dimExcept !== id
  return !dimExcept.includes(id)
}

const SEAM_PX = 0.6

function drawTriangle(ctx: Ctx2D, src: CanvasImageSource, rect: Rect, s: [V2, V2, V2], d: [V2, V2, V2]): void {
  const [s0, s1, s2] = s
  const [d0, d1, d2] = d
  const sx1 = s1.x - s0.x
  const sy1 = s1.y - s0.y
  const sx2 = s2.x - s0.x
  const sy2 = s2.y - s0.y
  const det = sx1 * sy2 - sx2 * sy1
  if (Math.abs(det) < 1e-9) return
  const dx1 = d1.x - d0.x
  const dy1 = d1.y - d0.y
  const dx2 = d2.x - d0.x
  const dy2 = d2.y - d0.y
  // M = D · S⁻¹
  const a = (dx1 * sy2 - dx2 * sy1) / det
  const c = (dx2 * sx1 - dx1 * sx2) / det
  const b = (dy1 * sy2 - dy2 * sy1) / det
  const dd = (dy2 * sx1 - dy1 * sx2) / det
  const e = d0.x - a * s0.x - c * s0.y
  const f = d0.y - b * s0.x - dd * s0.y
  const cx = (d0.x + d1.x + d2.x) / 3
  const cy = (d0.y + d1.y + d2.y) / 3
  const grow = (p: V2): V2 => {
    const vx = p.x - cx
    const vy = p.y - cy
    const m = Math.hypot(vx, vy) || 1
    return { x: p.x + (vx / m) * SEAM_PX, y: p.y + (vy / m) * SEAM_PX }
  }
  const g0 = grow(d0)
  const g1 = grow(d1)
  const g2 = grow(d2)
  ctx.save()
  ctx.beginPath()
  ctx.moveTo(g0.x, g0.y)
  ctx.lineTo(g1.x, g1.y)
  ctx.lineTo(g2.x, g2.y)
  ctx.closePath()
  ctx.clip()
  ctx.transform(a, b, c, dd, e, f)
  ctx.drawImage(src, rect.x, rect.y, rect.w, rect.h, rect.x, rect.y, rect.w, rect.h)
  ctx.restore()
}

/**
 * Draw every visible textured bone back to front. `getImage(styleId, name)` returns a loaded
 * image (or null while loading — that part is skipped this frame). Composes with the context's
 * current transform (e.g. a devicePixelRatio scale set by the caller).
 */
export function drawArmature(
  ctx: Ctx2D | null | undefined,
  root: SkfRoot,
  pose: Pose,
  world: WorldBone[],
  getImage: ImageLookup,
  opts: DrawOptions,
): void {
  if (!ctx) return
  const { view } = opts
  const baseAlpha = opts.alpha ?? 1
  const dimAlpha = opts.dimAlpha ?? 0.35
  for (const id of drawOrder(root, pose, world)) {
    const def = root.bones[id]!
    const visual = root.visuals[def.visuals_id]
    const pv = pose.visuals[def.visuals_id]
    const bone = world[id]
    if (!visual || !bone) continue
    const res = resolveTexture(root, pv?.tex ?? visual.tex, opts.activeStyles)
    if (!res || !(res.tex.size.x > 0 && res.tex.size.y > 0)) continue
    const img = getImage(res.styleId, res.tex.name)
    if (!img) continue
    const tint = opts.tintOverride ?? pv?.tint ?? visual.tint ?? { r: 1, g: 1, b: 1, a: 1 }
    const alpha = baseAlpha * tint.a * (dimmed(id, opts.dimExcept) ? dimAlpha : 1)
    if (!(alpha > 0)) continue
    let rect = sourceRect(img, res.tex)
    let src: CanvasImageSource = img
    if (!isWhite(tint)) {
      const tinted = tintedSource(img, rect, tint)
      if (tinted) {
        src = tinted
        rect = { x: 0, y: 0, w: Math.max(1, Math.round(rect.w)), h: Math.max(1, Math.round(rect.h)) }
      }
    }
    const size = res.tex.size
    ctx.save()
    ctx.globalAlpha = alpha
    const mesh = meshWorld(root, world, id, size)
    if (mesh && visual.vertices && visual.indices) {
      const verts = visual.vertices
      const idx = visual.indices
      const scr = mesh.map((p) => worldToScreen(view, p))
      const texPx = verts.map((v) => ({ x: rect.x + v.uv.x * rect.w, y: rect.y + v.uv.y * rect.h }))
      for (let t = 0; t + 2 < idx.length; t += 3) {
        const i0 = idx[t]!
        const i1 = idx[t + 1]!
        const i2 = idx[t + 2]!
        const s0 = texPx[i0]
        const s1 = texPx[i1]
        const s2 = texPx[i2]
        const d0 = scr[i0]
        const d1 = scr[i1]
        const d2 = scr[i2]
        if (!s0 || !s1 || !s2 || !d0 || !d1 || !d2) continue
        drawTriangle(ctx, src, rect, [s0, s1, s2], [d0, d1, d2])
      }
    } else {
      const q = quadOf(bone, visual, size)
      const c = worldToScreen(view, { x: q.cx, y: q.cy })
      const cos = Math.cos(q.rot)
      const sin = Math.sin(q.rot)
      const kx = view.scale * q.sx
      const ky = view.scale * q.sy
      // translate(c) · rotate(−rot) · scale(kx, ky), image drawn centred (y-down image space)
      ctx.transform(kx * cos, -kx * sin, ky * sin, ky * cos, c.x, c.y)
      ctx.drawImage(src, rect.x, rect.y, rect.w, rect.h, -size.x / 2, -size.y / 2, size.x, size.y)
    }
    ctx.restore()
  }
}

// ------------------------------------------------------------------------------ bone overlay

export interface BoneColors {
  fill: string
  stroke: string
  selected: string
  hovered: string
  joint: string
}

export const DEFAULT_BONE_COLORS: BoneColors = {
  fill: 'rgba(255,255,255,0.55)',
  stroke: 'rgba(24,24,32,0.85)',
  selected: '#f97316',
  hovered: '#fbbf24',
  joint: 'rgba(255,255,255,0.95)',
}

export interface BoneOverlayOptions {
  selected?: number | null
  hovered?: number | null
  colors?: Partial<BoneColors>
  /** Joint dot radius in screen px (the selected joint is 1.5×). */
  jointRadius?: number
  /** Draw hidden bones too (faded). Default false. */
  showHidden?: boolean
}

/**
 * Joint dots and "kites" (a slim diamond from each parent joint to its child joint), in screen
 * space over the drawn art. A kite takes the colour of the PARENT bone it represents.
 */
export function drawBones(
  ctx: Ctx2D | null | undefined,
  root: SkfRoot,
  world: WorldBone[],
  view: ViewXf,
  opts: BoneOverlayOptions = {},
): void {
  if (!ctx) return
  const colors = { ...DEFAULT_BONE_COLORS, ...opts.colors }
  const r0 = opts.jointRadius ?? 4
  const colorOf = (id: number): string =>
    id === opts.selected ? colors.selected : id === opts.hovered ? colors.hovered : colors.fill
  ctx.save()
  ctx.lineJoin = 'round'
  ctx.lineWidth = 1.25
  root.bones.forEach((b, i) => {
    const w = world[i]
    const p = b.parent_id >= 0 ? world[b.parent_id] : undefined
    if (!w || !p) return
    if (!opts.showHidden && w.hidden) return
    const a = worldToScreen(view, p.pos)
    const z = worldToScreen(view, w.pos)
    const vx = z.x - a.x
    const vy = z.y - a.y
    const L = Math.hypot(vx, vy)
    if (L < 2) return
    const ux = vx / L
    const uy = vy / L
    const half = Math.min(L * 0.12, 7)
    const mx = a.x + ux * L * 0.18
    const my = a.y + uy * L * 0.18
    ctx.beginPath()
    ctx.moveTo(a.x, a.y)
    ctx.lineTo(mx - uy * half, my + ux * half)
    ctx.lineTo(z.x, z.y)
    ctx.lineTo(mx + uy * half, my - ux * half)
    ctx.closePath()
    ctx.globalAlpha = w.hidden ? 0.35 : 1
    ctx.fillStyle = colorOf(b.parent_id)
    ctx.fill()
    ctx.strokeStyle = colors.stroke
    ctx.stroke()
  })
  world.forEach((w, i) => {
    if (!opts.showHidden && w.hidden) return
    const s = worldToScreen(view, w.pos)
    const sel = i === opts.selected
    const r = sel ? r0 * 1.5 : r0
    ctx.globalAlpha = w.hidden ? 0.35 : 1
    ctx.beginPath()
    ctx.arc(s.x, s.y, r, 0, Math.PI * 2)
    ctx.fillStyle = sel ? colors.selected : i === opts.hovered ? colors.hovered : colors.joint
    ctx.fill()
    ctx.strokeStyle = colors.stroke
    ctx.stroke()
  })
  ctx.restore()
}

